import { useCallback, useRef } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { UCP_SCHOOL_ID_HEADER } from '../lib/types/tenancy';
import { loginRequest } from '../auth/authConfig';
import { useSchoolContext } from '../components/SchoolContextProvider';

/**
 * Token- and tenancy-attached `fetch` for SPA data hooks (PRO-0050 / PRO-0030).
 * Acquires the MSAL access token silently (falling back to an interactive
 * redirect on `InteractionRequiredAuthError`, exactly like `useCurrentUser`) and
 * sets `Authorization: Bearer` plus `x-ucp-school-id` when an active school is
 * selected in `SchoolContextProvider`. Returns `null` when an interactive
 * redirect has been triggered (the page is navigating away).
 *
 * Callers may pass `opts.schoolId` to override the ambient switcher school for a
 * single call. This is needed where the acting school is the RESOURCE's school
 * rather than the globally-selected one — e.g. an OD acting on a request from
 * another of their schools in the aggregated ATR queue (PRO-0530 EP3). The server
 * still re-validates the school against the caller's remit, so the override never
 * widens access.
 */
const apiBaseUrl = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL ?? '');

export class NotSignedInError extends Error {
  constructor() {
    super('You are not signed in.');
    this.name = 'NotSignedInError';
  }
}

export interface ApiFetchOptions {
  /** Override the ambient switcher school for this call (`x-ucp-school-id`). */
  readonly schoolId?: string;
}

export type ApiFetch = (
  path: string,
  init?: RequestInit,
  opts?: ApiFetchOptions,
) => Promise<Response | null>;

export function useApiFetch(): ApiFetch {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const { activeSchoolId } = useSchoolContext();

  // MSAL returns a fresh `accounts` array on virtually every render (each silent
  // token acquisition dispatches an event into MsalProvider's reducer). Depending
  // on the `account` object identity would make this callback — and every hook
  // built on it — churn every render, which drives infinite fetch/render loops in
  // consumers whose effects depend on the fetcher. Depend on the stable primitive
  // `homeAccountId` instead and read the live account object through a ref.
  const accountId = account?.homeAccountId ?? null;
  const accountRef = useRef(account);
  accountRef.current = account;

  return useCallback(
    async (path: string, init?: RequestInit, opts?: ApiFetchOptions): Promise<Response | null> => {
      const account = accountRef.current;
      if (!account) {
        throw new NotSignedInError();
      }

      let accessToken: string;
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account });
        accessToken = result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ ...loginRequest, account });
          return null;
        }
        throw error;
      }

      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${accessToken}`);
      // A per-call override wins over the ambient switcher (the resource's own
      // school takes precedence — PRO-0530 EP3); otherwise fall back to the
      // selected school so existing callers are unchanged.
      const schoolId = opts?.schoolId ?? activeSchoolId;
      if (schoolId) {
        headers.set(UCP_SCHOOL_ID_HEADER, schoolId);
      }
      // Default JSON, but NEVER for a multipart upload: the browser must set
      // `multipart/form-data` with its own boundary, so leave FormData bodies
      // untouched (PRO-0560-1-2-0 evidence upload).
      if (
        init?.body !== undefined &&
        !(init.body instanceof FormData) &&
        !headers.has('Content-Type')
      ) {
        headers.set('Content-Type', 'application/json');
      }

      return fetch(`${apiBaseUrl}${path}`, { ...init, headers });
    },
    [accountId, instance, activeSchoolId],
  );
}
